/**
 * Turning a channel record into what a screen shows.
 *
 * Pure so the two rules that are easy to get wrong are actually tested:
 *
 *  - `state` is NOT the health signal. The harness only ever sets `listening`
 *    and `stopped`, and only for inbound channels. Outbound channels stay
 *    `idle` for their whole life and nothing anywhere sets `error`. A naive
 *    "not listening means down" rule paints every healthy outbound channel red.
 *  - Inbound and outbound carry different fields, so the endpoint has to come
 *    off the union arm rather than a shared optional.
 */
import type { Hl7Channel, Hl7InboundChannel, Hl7OutboundChannel } from './types'

/** Green is live, red is something went wrong, grey is neither. */
export type ChannelTone = 'green' | 'red' | 'grey'

export function channelTone(channel: Hl7Channel): ChannelTone {
  // Errors win over state: a listening channel that has been refusing messages
  // is not healthy, and `state` would never say so.
  if (channel.errors > 0 || channel.lastError !== null) return 'red'
  if (channel.state === 'listening') return 'green'
  // Covers idle and stopped. Also 'error', which the contract declares and the
  // harness never sets — handled so the union is exhaustive, not because it happens.
  return 'grey'
}

/**
 * What to call the resting state in the UI. An outbound channel is `idle`
 * permanently and by design, so calling it "stopped" or "down" would be a lie.
 */
export function channelStateLabel(channel: Hl7Channel): string {
  if (channel.direction === 'out') return channel.state === 'idle' ? 'Ready' : channel.state
  switch (channel.state) {
    case 'listening':
      return 'Listening'
    case 'stopped':
      return 'Stopped'
    default:
      return 'Idle'
  }
}

/** Inbound: the bound port. Outbound: the target we dial. */
export function channelEndpoint(channel: Hl7Channel): string {
  return channel.direction === 'in' ? `:${channel.port}` : channel.target
}

/**
 * The port an outbound channel dials, dug out of its `host:port` target.
 *
 * Outbound channels carry no `port` field at all — the harness only binds one
 * for an inbound listener — so `target` is the only place the number exists.
 * Anchored to the end of the string, and refusing a colon immediately before
 * the digits, so a bare IPv6 host (`::1`) does not read its last group as a
 * port while a bracketed one (`[::1]:5005`) still does.
 *
 * Null rather than an empty string when there is nothing to read: a caller then
 * has to handle the absence, instead of concatenating `:undefined` into a label.
 */
export function targetPort(target: string | null | undefined): number | null {
  if (!target) return null
  const match = /(?:^|[^:]):(\d{1,5})$/.exec(target.trim())
  if (!match) return null
  const port = Number(match[1])
  return port >= 1 && port <= 65535 ? port : null
}

/**
 * How an outbound channel reads in a picker: `beacon-out · :5005 · ORM, ORU`.
 *
 * The port segment matches the Receive filter's `{id} · :{port}` so a channel
 * can be identified by port on either side of the tab. It is dropped entirely
 * when the target has no parseable port, leaving `{id} · {types}`.
 *
 * Typed to the outbound arm rather than `Hl7Channel` deliberately. The Receive
 * filter builds its own label inline and is out of scope here, so a union-typed
 * builder would carry an inbound branch that nothing calls.
 */
export function outboundChannelLabel(channel: Hl7OutboundChannel): string {
  const port = targetPort(channel.target)
  const parts = [channel.id]
  if (port !== null) parts.push(`:${port}`)
  if (channel.types.length > 0) parts.push(channel.types.join(', '))
  return parts.join(' · ')
}

export function isInbound(channel: Hl7Channel): channel is Hl7InboundChannel {
  return channel.direction === 'in'
}

export function isOutbound(channel: Hl7Channel): channel is Hl7OutboundChannel {
  return channel.direction === 'out'
}

export function inboundChannels(channels: Hl7Channel[]): Hl7InboundChannel[] {
  return channels.filter(isInbound)
}

/**
 * Empty under the shipped `channels.json`, which carries inbound channels only.
 * That is why the Send panel has to say so rather than render an empty picker:
 * the fix is running `npm run mllp:dev`, not reconfiguring anything.
 */
export function outboundChannels(channels: Hl7Channel[]): Hl7OutboundChannel[] {
  return channels.filter(isOutbound)
}

/** `AA 3 · AE 1`, or null when nothing has been acknowledged yet. */
export function formatAcks(acks: Record<string, number>): string | null {
  const entries = Object.entries(acks).filter(([, count]) => count > 0)
  if (entries.length === 0) return null
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, count]) => `${code} ${count}`)
    .join(' · ')
}
